import {
  Box,
  Button,
  ButtonBase,
  CardActions,
  CardContent,
  Checkbox,
  Divider,
  List,
  ListItem,
  Menu,
  MenuItem,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { TemplateReportItem } from "../@types";
import { DriveFolderUploadIcon, FolderOffIcon, SaveAsIcon, SaveIcon } from "../icons";
import { overviewClassListState, selectedReportListState } from "../recoil";
import { createTemplate, getTemplate, getTemplates } from "../services/reportService";
import { formatDate } from "../utils/utils";
import { EDITED_TEXT_FILTER, REPORT_CONTENTS } from "./report/ReportContents";
import CustomButton from "./customComponent/CustomButton";
import CustomInput from "./customComponent/CustomInput";
import { ContentCopy, DeleteForever, DeleteOutline, Edit, LinkOff } from "@mui/icons-material";
import { clearImgData, getMyModuleServer, removeAIModules } from "common-lib";

interface MouseClickListProps {
  contextMenu?: { x: number; y: number } | null;
  anchorEl?: HTMLElement | null;
  open: boolean;
  category: string;
  onClose: () => void;
  onCopy?: () => void;
  onEdit: () => void;
  onDelete: () => void;
  existModuleIds?: Record<number, string>;
  onModuleDelete?: () => void;
  dataId?: number;
}

export default function MouseClickList({
  contextMenu,
  open,
  category,
  onClose,
  onCopy,
  onEdit,
  onDelete,
  existModuleIds,
  onModuleDelete,
  dataId,
}: MouseClickListProps) {
  const [isMatched, setIsMatched] = useState(false);
  const [aiServerAddress, setAiServerAddress] = useState<string | null>(null);

  // 프로젝트에 연결된 AI 서버 확인 (있을 때만 제거 메뉴 노출)
  // ponytail: uuid=`${id}` 기준, 분석 프로젝트의 `${id}_${label}` 모듈은 대상 아님
  useEffect(() => {
    if (!open || category !== "project" || !dataId) return;
    let cancelled = false;
    getMyModuleServer(`${dataId}`, null)
      .then((res: any) => !cancelled && setAiServerAddress(res?.module && res?.serverAddress ? res.serverAddress : null))
      .catch(() => !cancelled && setAiServerAddress(null));
    return () => {
      cancelled = true;
    };
  }, [open, category, dataId]);

  const handleRemoveAIServer = async () => {
    if (!aiServerAddress || !window.confirm(t("RemoveAIOptionConfirm", { server: aiServerAddress }))) return;
    try {
      await clearImgData(aiServerAddress, `${dataId}`);
      await removeAIModules(aiServerAddress, `${dataId}`);
      setAiServerAddress(null);
      toast.success(t("RemoveAIOption"));
    } catch (error: any) {
      console.error(error);
      toast.error(error?.message ?? String(error));
    }
  };

  useEffect(() => {
    if (!dataId || !existModuleIds) return;
    setIsMatched(!!existModuleIds[dataId]);
  }, [dataId, existModuleIds]);

  if (!open || !contextMenu) return null;

  const menuItems = [
    ...(category === "project"
      ? [{ label: t("Copy"), icon: <ContentCopy sx={{ fontSize: 14 }} />, onClick: onCopy }]
      : []),
    { label: t("Edit"), icon: <Edit sx={{ fontSize: 14 }} />, onClick: onEdit },
    { label: t("Delete"), icon: <DeleteOutline sx={{ fontSize: 14 }} />, onClick: onDelete, danger: true },
    ...(category === "train" && isMatched
      ? [
          {
            label: t("ModuleDelete"),
            icon: <DeleteForever sx={{ fontSize: 14 }} />,
            onClick: onModuleDelete,
            danger: true,
          },
        ]
      : []),
    ...(category === "project" && aiServerAddress
      ? [
          {
            label: t("RemoveAIOption"),
            icon: <LinkOff sx={{ fontSize: 14 }} />,
            onClick: handleRemoveAIServer,
            danger: true,
          },
        ]
      : []),
  ];

  return (
    <>
      {/* 배경 클릭시 닫기 */}
      <Box
        sx={{ position: "fixed", inset: 0, zIndex: 1299 }}
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />

      <Paper
        elevation={0}
        sx={{
          position: "fixed",
          top: contextMenu.y,
          left: contextMenu.x,
          zIndex: 1300,
          minWidth: 140,
          py: 0.5,
          border: "0.5px solid",
          borderColor: "grey.200",
          borderRadius: "8px",
          boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
          overflow: "hidden",
        }}
      >
        {menuItems.map((item, idx) => (
          <Box key={idx}>
            {/* 삭제 계열 앞에 구분선 */}
            {item.danger && idx > 0 && <Divider sx={{ my: 0.5, borderColor: "grey.100" }} />}
            <ButtonBase
              onClick={() => {
                item.onClick?.();
                onClose();
              }}
              sx={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.5,
                py: 0.75,
                fontSize: 13,
                color: item.danger ? "error.main" : "text.primary",
                justifyContent: "flex-start",
                "&:hover": {
                  bgcolor: item.danger ? "error.50" : "grey.50",
                },
              }}
            >
              <Box sx={{ color: item.danger ? "error.main" : "text.secondary", display: "flex" }}>{item.icon}</Box>
              {item.label}
            </ButtonBase>
          </Box>
        ))}
      </Paper>
    </>
  );
}

interface ClickListProps {
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  onClick?: () => void;
  classList?: string[];
}

export function ReportPrintList({ anchorEl, open, onClose, onClick }: ClickListProps) {
  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      <MenuItem onClick={onClick}>{t("PDF")}</MenuItem>
      <MenuItem>{t("Excel")}</MenuItem>
    </Menu>
  );
}

export function ReportSettingList({ anchorEl, open, onClose }: ClickListProps) {
  const [selectedReportList, setSelectedReportList] = useRecoilState(selectedReportListState);
  const [templateName, setTemplateName] = useState<string>("");
  const [templateList, setTemplateList] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [openSaveCurrentState, setOpenSaveCurrentState] = useState<boolean>(false);
  const [openLoadSavedTemplate, setOpenLoadSavedTemplate] = useState<boolean>(false);

  const fetchTemplates = async () => {
    try {
      const response = await getTemplates();
      console.log(response);
      const filteredTemplates = response.templates.filter((item: any) => item.created_by != null);
      setTemplateList(filteredTemplates);
    } catch (error) {
      console.error(error);
    }
  };

  const handleTemplateChange = (id: number) => {
    setSelectedTemplate(templateList.find((template) => template.id === id));
  };

  const handleSelectSavedTemplate = async (id: number) => {
    try {
      const response = await getTemplate(id);
      setSelectedTemplate(response.template);
      // 구버전 항목(현재 리포트 구성에 없는 type)은 제외
      const parsedContents = JSON.parse(response.template.contents || "[]").filter((item: any) => item.type in REPORT_CONTENTS);
      const contentsWithIds = parsedContents.map((item: any) => {
        const uniqueRandomNumber = String(Math.floor(100000 + Math.random() * 900000));
        return {
          ...item,
          id: `${item.type}-${uniqueRandomNumber}`,
        };
      });
      setSelectedReportList(contentsWithIds);
      setOpenLoadSavedTemplate(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleApply = async () => {
    try {
      if (templateName === "") {
        toast.warning("템플릿 이름을 입력해주세요.");
        return;
      }

      const userInfo = JSON.parse(localStorage.getItem("userInfo") || "");
      console.log(userInfo.id);

      const templateItems = selectedReportList.map(({ gridSize, type, filter: rawFilter }, idx) => {
        const filter = rawFilter?.filter((f) => f.type !== EDITED_TEXT_FILTER); // 수정한 소견은 프로젝트 리포트 전용
        const templateItem: TemplateReportItem = {
          index: idx + 1,
          gridSize,
          type,
          filter,
        };

        if (filter) {
          templateItem.filter = filter;
        }

        return templateItem;
      });
      await createTemplate({
        name: templateName,
        contents: JSON.stringify(templateItems), // DB 컬럼이 문자열
        user_id: userInfo.id,
      });
      setOpenSaveCurrentState(false);
      onClose();
      setTemplateName("");
    } catch (error) {
      console.error(error);
    }
  };

  const handleSaveCurrentState = () => {
    setOpenSaveCurrentState(true);
  };

  const handleLoadSavedTemplate = () => {
    fetchTemplates();
    setOpenLoadSavedTemplate(true);
    setSelectedTemplate(null);
  };

  return (
    <>
      <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
        <MenuItem onClick={handleSaveCurrentState}>
          <SaveAsIcon /> <Typography>{t("SaveCurrentState")}</Typography>
        </MenuItem>
        <MenuItem onClick={handleLoadSavedTemplate}>
          <DriveFolderUploadIcon />
          <Typography>{t("LoadSavedTemplate")}</Typography>
        </MenuItem>
      </Menu>
      <Modal open={openSaveCurrentState} onClose={() => setOpenSaveCurrentState(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "400px",
            height: "200px",
            backgroundColor: "var(--report-modal-bg)",
            borderRadius: "10px",
            padding: "20px",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {t("SaveTemplate")}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <CustomInput
              required={false}
              subTitle={t("TemplateName")}
              value={templateName}
              setValue={setTemplateName}
              width="100%"
              height="40px"
            />
            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: "10px",
                justifyContent: "flex-end",
              }}
            >
              <Button variant="outlined" onClick={() => setOpenSaveCurrentState(false)}>
                {t("Cancel")}
              </Button>
              <Button variant="contained" onClick={handleApply}>
                <SaveIcon />
                {t("Save")}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>
      <Modal open={openLoadSavedTemplate} onClose={() => setOpenLoadSavedTemplate(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "400px",
            height: "350px",
            backgroundColor: "var(--report-modal-bg)",
            borderRadius: "10px",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
            {t("LoadTemplate")}
          </Typography>
          <Box
            sx={{
              overflowY: "auto",
              flex: 1,
              mb: 1,
            }}
          >
            {templateList.length === 0 ? (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "var(--text-secondary-color)",
                  p: 3,
                }}
              >
                <FolderOffIcon
                  sx={{
                    fontSize: 60,
                    mb: 2,
                    color: "action.disabled",
                  }}
                />
                <Typography
                  variant="h6"
                  sx={{
                    mb: 1,
                    color: "var(--text-secondary-color)",
                    textAlign: "center",
                  }}
                >
                  {t("MsgNoTemplate")}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "var(--text-secondary-color)",
                    textAlign: "center",
                  }}
                >
                  {t("MsgNoSavedTemplate")}
                </Typography>
              </Box>
            ) : (
              templateList.map((template) => (
                <ListItem
                  key={template.id}
                  value={template.id}
                  sx={{
                    height: "50px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "8px 16px",
                    backgroundColor:
                      selectedTemplate?.id === template.id ? "rgba(25, 118, 210, 0.08)" : "transparent",
                    "&:hover": {
                      backgroundColor:
                        selectedTemplate?.id === template.id ? "rgba(25, 118, 210, 0.12)" : "rgba(0, 0, 0, 0.04)",
                    },
                    border: "1px solid var(--dk-border, rgba(0, 0, 0, 0.12))",
                    borderRadius: "10px",
                    mb: 1,
                    cursor: "pointer",
                  }}
                  onClick={() => handleTemplateChange(template.id)}
                >
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {template.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "var(--text-secondary-color)",
                      fontSize: "0.875rem",
                    }}
                  >
                    {formatDate(template.create_at)}
                  </Typography>
                </ListItem>
              ))
            )}
          </Box>
          {templateList.length > 0 && (
            <Box
              sx={{
                mt: "auto",
                borderTop: "1px solid var(--dk-border, rgba(0, 0, 0, 0.12))",
                pt: 1,
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
              }}
            >
              <Button variant="outlined" onClick={() => setOpenLoadSavedTemplate(false)}>
                {t("Cancel")}
              </Button>
              <Button variant="contained" onClick={() => handleSelectSavedTemplate(selectedTemplate?.id)}>
                {t("Select")}
              </Button>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
}

export function OverviewSettingList({ anchorEl, open, onClose, classList }: ClickListProps) {
  const [tempClassList, setTempClassList] = useState<string[]>([]);
  const [selectedClassList, setSelectedClassList] = useRecoilState(overviewClassListState);

  useEffect(() => {
    if (selectedClassList && selectedClassList.length > 2) {
      setTempClassList(selectedClassList.slice(0, 2));
      console.log(selectedClassList.slice(0, 2));
    } else {
      setTempClassList(selectedClassList!);
      console.log(selectedClassList!);
    }
    console.log(selectedClassList);
  }, [selectedClassList]);

  const handleCheckboxChange = (label: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setTempClassList((prev) => [...prev, label]);
    } else {
      setTempClassList((prev) => prev.filter((item) => item !== label));
    }
  };

  const handleApply = () => {
    setSelectedClassList(tempClassList);
    onClose();
  };

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
      <Typography variant="body1" sx={{ p: 1, pr: 2, pl: 2 }}>
        {t("DisplayedItems")}
      </Typography>
      <CardContent sx={{ width: "300px", p: 1, height: "210px", overflowY: "auto" }}>
        <Box>
          <List>
            {classList?.map((label) => (
              <ListItem sx={{ p: 0, borderBottom: "1px solid var(--dk-border, rgba(0,0,0,0.12))" }}>
                <Checkbox checked={tempClassList.includes(label)} onChange={handleCheckboxChange(label)} />
                {label} {t("Class")}
              </ListItem>
            ))}
          </List>
        </Box>
      </CardContent>
      <CardActions>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row-reverse",
            gap: "3px",
          }}
        >
          <CustomButton text={t("Apply")} variant="contained" onClick={handleApply} />
          <CustomButton text={t("Cancel")} variant="outlined" onClick={onClose} />
        </Box>
      </CardActions>
    </Menu>
  );
}
